import { memo, useMemo } from "react";
import { motion } from "framer-motion";
import styles from "./RamadanDecoration.module.css";

/**
 * Generates random star positions
 */
const generateStars = (count) => {
  const stars = [];
  for (let i = 0; i < count; i++) {
    stars.push({
      id: i,
      x: Math.random() * 70,
      y: Math.random() * 50,
      size:
        Math.random() < 0.6
          ? "small"
          : Math.random() < 0.85
            ? "medium"
            : "large",
      duration: 2 + Math.random() * 3,
      delay: Math.random() * 4,
    });
  }
  return stars;
};

/**
 * Star component
 */
const Star = memo(({ x, y, size, duration, delay }) => {
  const sizeClass =
    size === "small"
      ? styles.starSmall
      : size === "large"
        ? styles.starLarge
        : "";

  return (
    <div
      className={`${styles.star} ${sizeClass}`}
      style={{
        left: `${x}%`,
        top: `${y}%`,
        "--duration": `${duration}s`,
        "--delay": `${delay}s`,
      }}
      aria-hidden="true"
    />
  );
});

Star.displayName = "Star";

/**
 * Lantern component
 */
const Lantern = memo(({ size, className }) => {
  const chainClass =
    size === "small"
      ? styles.chainSmall
      : size === "large"
        ? styles.chainLarge
        : styles.chainMedium;

  const bodyClass =
    size === "small"
      ? styles.lanternBodySmall
      : size === "large"
        ? styles.lanternBodyLarge
        : styles.lanternBodyMedium;

  return (
    <div className={`${styles.lantern} ${className}`}>
      <div className={`${styles.chain} ${chainClass}`} aria-hidden="true" />

      <div className={`${styles.lanternBody} ${bodyClass}`}>
        <div className={styles.topCap} aria-hidden="true" />

        <div className={styles.glassBody}>
          <div className={styles.glassBand} aria-hidden="true" />

          <motion.div
            className={styles.innerGlow}
            initial={{ opacity: 0.6 }}
            animate={{ opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            aria-hidden="true"
          />

          <motion.div
            className={styles.flame}
            animate={{
              scaleY: [1, 1.12, 0.92, 1.08, 1],
              scaleX: [1, 0.9, 1.06, 0.94, 1],
            }}
            transition={{ duration: 0.7, repeat: Infinity, ease: "easeInOut" }}
            aria-hidden="true"
          />

          <div className={styles.candle} aria-hidden="true" />
        </div>

        <div className={styles.bottomCap} aria-hidden="true" />

        <motion.div
          className={styles.lightRays}
          initial={{ opacity: 0.35 }}
          animate={{ opacity: [0.35, 0.6, 0.35] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          aria-hidden="true"
        />
      </div>
    </div>
  );
});

Lantern.displayName = "Lantern";

/**
 * Quarter Moon SVG (Ramadan-style)
 * - Chunkier than a thin crescent
 * - Still glowing + animated
 */
const QuarterMoon = memo(() => (
  <svg
    viewBox="0 0 100 100"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={styles.crescentSvg}
    aria-label="Quarter moon"
    role="img"
  >
    <defs>
      <linearGradient
        id="quarterMoonGradient"
        x1="0%"
        y1="0%"
        x2="100%"
        y2="100%"
      >
        <stop offset="0%" stopColor="#FFFBEB" />
        <stop offset="35%" stopColor="#FDE68A" />
        <stop offset="70%" stopColor="#FBBF24" />
        <stop offset="100%" stopColor="#D97706" />
      </linearGradient>

      <filter id="quarterMoonGlow" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="2.2" result="blur" />
        <feComposite in="SourceGraphic" in2="blur" operator="over" />
      </filter>

      <radialGradient id="quarterMoonOuter" cx="50%" cy="50%" r="55%">
        <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.35" />
        <stop offset="55%" stopColor="#FDE68A" stopOpacity="0.18" />
        <stop offset="100%" stopColor="#FBBF24" stopOpacity="0" />
      </radialGradient>
    </defs>

    {/* Soft outer glow */}
    <motion.circle
      cx="50"
      cy="50"
      r="46"
      fill="url(#quarterMoonOuter)"
      initial={{ scale: 0.92, opacity: 0.18 }}
      animate={{ scale: [0.92, 1.06, 0.92], opacity: [0.18, 0.32, 0.18] }}
      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
    />

    {/* Quarter-moon body (thick crescent / quarter look) */}
    <motion.path
      d="
        M52 10
        A40 40 0 1 1 52 90
        A26 26 0 1 0 52 10
      "
      fill="url(#quarterMoonGradient)"
      filter="url(#quarterMoonGlow)"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.1, ease: "easeOut" }}
    />

    <motion.path
      d="
        M52 10
        A40 40 0 1 1 52 90
        A26 26 0 1 0 52 10
    "
      fill="none"
      stroke="#FFF7D6"
      strokeWidth="2.2"
      strokeOpacity="0.55"
    />

    {/* Gentle highlight edge */}
    <path
      d="
        M52 14
        A36 36 0 1 1 52 86
        A23 23 0 1 0 52 14
      "
      fill="none"
      stroke="#FFFBEB"
      strokeOpacity="0.25"
      strokeWidth="1.4"
    />

    {/* Small star (optional) */}
    <motion.polygon
      points="34,32 36,37 41,37 37,40 38,45 34,42 30,45 31,40 27,37 32,37"
      fill="#FFFBEB"
      initial={{ opacity: 0.55, scale: 0.92 }}
      animate={{ opacity: [0.55, 1, 0.55], scale: [0.92, 1.08, 0.92] }}
      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      style={{ transformOrigin: "34px 38px" }}
    />
  </svg>
));

QuarterMoon.displayName = "QuarterMoon";

/**
 * Decorative Star SVG
 */
const DecorativeStar = memo(({ size = 12, delay = 0 }) => (
  <div
    className={styles.decorativeStar}
    style={{ width: size, height: size, "--delay": `${delay}s` }}
  >
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <polygon
        points="12,2 14,9 21,9 15,14 17,21 12,17 7,21 9,14 3,9 10,9"
        fill="#FDE68A"
        opacity="0.9"
      />
    </svg>
  </div>
));

DecorativeStar.displayName = "DecorativeStar";

/**
 * Main Ramadan Decoration Component
 */
const RamadanDecoration = memo(({ position = "top-left" }) => {
  const stars = useMemo(() => generateStars(20), []);

  const positionClass = useMemo(() => {
    switch (position) {
      case "top-right":
        return styles.topRight;
      case "bottom-left":
        return styles.bottomLeft;
      case "bottom-right":
        return styles.bottomRight;
      default:
        return styles.topLeft;
    }
  }, [position]);

  return (
    <div
      className={`${styles.container} ${positionClass}`}
      role="presentation"
      aria-hidden="true"
    >
      {/* Night Sky Gradient */}
      <div className={styles.skyGradient} />

      {/* Ambient Glow */}
      <div className={styles.ambientGlow} />

      {/* Stars */}
      <div className={styles.starsContainer}>
        {stars.map((star) => (
          <Star
            key={star.id}
            x={star.x}
            y={star.y}
            size={star.size}
            duration={star.duration}
            delay={star.delay}
          />
        ))}
      </div>

      {/* Lanterns */}
      <div className={styles.lanternsWrapper}>
        <Lantern size="small" className={styles.lanternFirst} />
        <Lantern size="medium" className={styles.lanternSecond} />
        <Lantern size="large" className={styles.lanternThird} />
      </div>

      {/* Quarter Moon (replaces sun/yellow circle) */}
      <div className={styles.moonWrapper}>
        <motion.div
          className={styles.moonGlow}
          initial={{ scale: 0.85, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        />
        <QuarterMoon />
      </div>

      {/* Decorative Stars */}
      <DecorativeStar size={14} delay={0} />
      <DecorativeStar size={10} delay={1.2} />
      <DecorativeStar size={8} delay={2.4} />

      {/* Hover Area */}
      <div className={styles.hoverArea} />

      <div className={styles.ramadanText}>
        رمضان كريم
      </div>
    </div>
  );
});

RamadanDecoration.displayName = "RamadanDecoration";

export default RamadanDecoration;
